<template>
  <page>
    <span slot="page-title">
      2018 Election Forecasting Simulator Report
      <v-chip
        style="font-size: 0.5em; font-weight: 400; letter-spacing: 0.05em; height: 22px;"
        color="accent" text-color="white">Beta
      </v-chip>
      <div style="font-size: 0.5em; font-weight: 500; text-align: center; color: #757575; letter-spacing: 0.05em;">
        The 2018 Forecasting Simulator is in Beta - Find a bug?
        <a href="https://decisiondeskhq.com/contact-us/" target="_blank">Contact us</a>
      </div>
    </span>
    <div v-if="scenario && expenditures.length > 0">
      <div class="report-overview">
        <!-- Summary -->
        <h3 style="text-align: center;"></h3>
        <div id="summary">
          <div class="summary-item">
            <div class="summary-item-label">Scenario: </div>
            <h3>{{ scenario.name }}</h3>
          </div>
          <div class="summary-item">
            <div class="summary-item-label">Actual 2018 Outcome: </div>
            <div>
              <span class="outcome-party" style="color: #66ABCF;">Dem </span>
              <span class="outcome-score">{{ chamber === 'house' ? 235 : 47 }}</span>
              <span style="font-weight: 600; color: #333; margin: 0 5px; letter-spacing: 0.05em;"> vs </span>
              <span class="outcome-score">{{ chamber === 'house' ? 200 : 53 }}</span>
              <span class="outcome-party" style="color: #FA3138;"> GOP</span>
            </div>
          </div>

          <div class="summary-item">
            <div class="summary-item-label">{{ summary.label }}: </div>
            <div>
              <span class="outcome-party" style="color: #66ABCF;">Dem </span>
              <span class="outcome-score">{{ getOutcomeChange(summary.expected, 'Dem', chamber === 'house' ? 235 : 47) }}</span>
              <span style="font-weight: 600; color: #333; margin: 0 5px; letter-spacing: 0.05em;"> vs </span>
              <span class="outcome-score">{{ getOutcomeChange(summary.expected, 'GOP', chamber === 'house' ? 200 : 53) }}</span>
              <span class="outcome-party" style="color: #FA3138;"> GOP</span>
            </div>
          </div>

          <div class="summary-item mb-5">
            <div class="summary-item-label">Seat Changes: </div>
              <div class="mt-1" style="display: inline-block;">
                <span class="ml-2">
                  <span style="color: #57AEA6;" v-if="summary.favored.change > 0">+</span>
                  <span :style="getColor(summary.favored.change)">{{ summary.favored.change }}</span>
                  <span style="font-size: 1em; font-weight: 500; color: #333;">
                    {{ summary.favored.party }}
                    Favored Seats,
                  </span>
                </span>
                <span class="ml-2">
                  <span style="color: #57AEA6;" v-if="summary.expected.change > 0">+</span>
                  <span :style="getColor(summary.expected.change)">{{ summary.expected.change }}</span>
                  <span style="font-size: 1em; font-weight: 500; color: #333;">
                    {{ summary.expected.party }}
                    Expected Seats
                  </span>
                </span>
              </div>
              <div style="font-size: 0.9em; font-style: italic; color: #666;">
                Favored seats are races where there is a greater than 50% chance of victory. Expected seats are the aggregate number of races the a party would win on average given the probability of victory in each race.
            </div>
          </div>

        <!-- Preamble -->
        <div>
          On November 6th, Democrats won
          {{ chamber === 'house' ? 235 : 47 }}
          {{ chamber }}
          seats compared to
          {{ chamber === 'house' ? 200 : 53 }} for Republicans. If the election were re-run with these parameters, we would instead expect
          {{ getOutcomeChange(summary.expected, 'Dem', chamber === 'house' ? 235 : 47) }} Democratic wins versus
          {{ getOutcomeChange(summary.expected, 'GOP', chamber === 'house' ? 200 : 53) }} Republican wins.
        </div>

        <div class="mt-2 mb-2">
          In this scenario the Republicans spent {{ totalSpend.gop | money }}
          <template v-if="maxSpend.gop === totalSpend.gop">
            (same as they actually did in 2018)
          </template>
          <template v-else>
            (compared to the {{ maxSpend.gop | money }} they actually spent in 2018)
          </template>
          and the Democrats spent {{ totalSpend.dem | money }}
          <template v-if="maxSpend.dem === totalSpend.dem">
            (same as they actually did in 2018).
          </template>
          <template v-else>
            (compared to the {{ maxSpend.dem | money }} they actually spent in 2018).
          </template>

          <template v-if="totalSpend.gop !== maxSpend.gop || totalSpend.dem !== maxSpend.dem">
            In this scenario
            <template v-if="totalSpend.gop > maxSpend.gop && totalSpend.dem > maxSpend.dem">
              the Republicans and Democrats spent more than they did in reality so this simulation is not realistic.
            </template>
            <template v-else-if="totalSpend.dem > maxSpend.dem">
              the Democrats spent more than they did in reality so this simulation is not realistic.
            </template>
            <template v-else-if="totalSpend.gop > maxSpend.gop">
              the Republicans spent more than they did in reality so this simulation is not realistic.
            </template>

            <template v-if="totalSpend.gop < maxSpend.gop && totalSpend.dem < maxSpend.dem">
              The Republicans and Democrats spent less than they did in reality, leaving {{ (maxSpend.gop - totalSpend.gop) | money }} and {{ (maxSpend.dem = totalSpend.dem) | money }}, respectively, on the table.
            </template>
            <template v-else-if="totalSpend.dem < maxSpend.dem">
              The Democrats spent less than they did in reality, leaving {{ (maxSpend.dem - totalSpend.dem) | money }} on the table.
            </template>
            <template v-else-if="totalSpend.gop < maxSpend.gop">
              The Republicans spent less than they did in reality, leaving {{ (maxSpend.gop - totalSpend.gop) | money }} on the table.
            </template>
          </template>
        </div>

        <div class="mb-2">
          Of the {{ seatsChanged.length }} races modified, {{ strings.prop === 'dem' ? seatsLessThan.length : seatsGreaterThan.length  }} moved towards the Republicans and
          {{ strings.prop === 'dem' ? seatsGreaterThan.length : seatsLessThan.length }} moved towards the Democrats.
          <template v-if="scenario.mode !== 'god'">In the most improved seats list below are the races where money was reallocated to and the probability for victory increased the most. In the most triaged seats list you can see the races where the most money was removed.</template>
        </div>

          <div class="top-list-wrapper mt-5">
            <div class="list-wrapper" v-for="(list, index) in lists" :key="index" v-if="list.rows.length > 0">
              <h5 class="list-header">{{ list.label }}</h5>
              <div class="list-description">{{ list.description }}</div>
              <div class="list-item"
                v-for="(item, index2) in list.rows" :key="index2">
                <span class="list-number">{{ index2 + 1 }}</span>
                <span>
                  <div>
                    <span class="list-item-key">Race: </span>
                    <span class="list-item-text">{{ item.race }} </span>
                  </div>
                  <div>
                    <span class="list-item-key">GOP Money Spent: </span>
                    <span class="list-item-text">{{ item.gopSpent | money }} </span>
                    <span class="list-item-text" :style="getColor(item.gopMoneyChange)">
                      (<template v-if="item.gopMoneyChange > 0">+</template>
                      {{ item.gopMoneyChange | money }})
                    </span>
                  </div>
                  <div>
                    <span class="list-item-key">Dem Money Spent: </span>
                    <span class="list-item-text">{{ item.demSpent | money }} </span>
                    <span class="list-item-text" :style="getColor(item.demMoneyChange)">
                      (<template v-if="item.demMoneyChange > 0">+</template>
                      {{ item.demMoneyChange | money }})
                    </span>
                  </div>
                  <div>
                    <span class="list-item-key">Probability of Victory: </span>
                    <span class="list-item-text">{{ item.winProb | votePercent }} </span>
                    <span class="list-item-text" :style="getColor(item.perc)">
                      (<template v-if="item.perc > 0">+</template>
                      {{ item.perc | votePercent }})
                    </span>
                  </div>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <v-divider class="mb-4"></v-divider>

      <div class="mb-5" style="text-align: center;">
        <h3>Share this report</h3>
        <expenditure-share :mode="scenario.mode" :url="shareUrl.replace('report?', 'report/?')" :shareText="shareText"></expenditure-share>
      </div>

      <div v-if="$route.params.scenario" style="text-align: center;">
        <v-divider class="mb-4"></v-divider>
        <!-- Show save related content only if coming directy from the calculator view -->
        <h3>Save your report</h3>

        <div style="margin: 40px 0">
          <login-signup @login="updateScenario" v-if="!$auth.authenticated"></login-signup>
          <div v-else-if="updatingScenario">
            <h5>Loading</h5>
          </div>
          <div v-else>
            <h5>Your report has been saved and will be available for edit on the calculator page</h5>
          </div>
        </div>
      </div>

      <v-divider class="mb-4"></v-divider>

      <h3 style="text-align: center;">View the entire expenditures table</h3>
      <expenditures-table
        :readonly="true"
        :expenditures="expenditures"
        :editableFields="[]">
      </expenditures-table>

      <v-divider class="mb-5"></v-divider>

      <v-btn class="mb-5" block color="primary" outline dark @click="$router.push({ name: 'calculator' })">New Scenario</v-btn>
    </div>
  </page>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import { ExpenditureShare, ExpendituresTable } from '@/components/common/calculator'
import { Page, LoginSignup } from '@/components'
import API from '@/api'
import calculator from '@/mixins/calculator'

const mixins = [
  calculator
]

const computed = {
  ...mapGetters({
    chamber: 'calculator/chamber'
  }),
  strings () {
    return {
      gop: {
        prop: 'gop',
        label: 'GOP',
        full: 'Republicans',
        change: 'gopChange',
        expenditures: 'gopExpenditures',
        name: 'My 2018 Republican Spending Plan'
      },
      dem: {
        prop: 'dem',
        label: 'Dem',
        full: 'Democrats',
        change: 'demChange',
        expenditures: 'demExpenditures',
        name: 'My 2018 Democratic Spending Plan'
      }
    }[this.scenario.mode === 'god' ? 'gop' : this.scenario.mode]
  },
  summary () {
    if (this.scenario.mode === 'god') {
      return {
        label: 'New Expected 2018 Outcome',
        favored: {
          change: this.favored.gop.change,
          party: 'GOP'
        },
        expected: {
          change: Math.round(this.expected.gop.change),
          party: 'GOP'
        }
      }
    }
    return {
      label: 'New Expected 2018 Outcome',
      favored: {
        change: this.favored[this.strings.prop].change,
        party: this.strings.label
      },
      expected: {
        change: Math.round(this.expected[this.strings.prop].change),
        party: this.strings.label
      }
    }
  },
  lists () {
    // const percentage = value => (value > 0 ? '+' : '') + (value * 100).toFixed(2) + '%'
    // const money = value => Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    if (this.scenario.mode === 'god') {
      const mostImprovedGop = this.expenditures.filter(r => (r.gopChange.value !== 0 || r.demChange.value !== 0) && r.probabilityDelta.value > 0)
        .sort((a, b) => b.probabilityDelta.value > a.probabilityDelta.value ? 1 : -1).slice(0, 10)
      const mostImprovedDem = this.expenditures.filter(r => (r.gopChange.value !== 0 || r.demChange.value !== 0) && r.probabilityDelta.value < 0)
        .sort((a, b) => a.probabilityDelta.value > b.probabilityDelta.value ? 1 : -1).slice(0, 10)
      return [
        {
          label: 'Seats Shifting Towards GOP',
          rows: mostImprovedGop.map((race) => {
            return {
              race: race.name,
              party: 'Republicans',
              gopSpent: race.gopExpenditures,
              gopMoneyChange: race.gopChange.value,
              demSpent: race.demExpenditures,
              demMoneyChange: race.demChange.value,
              winProb: race.finalProbability,
              perc: race.probabilityDelta.value
            }
            // return `${race.name} (${money(race.gopChange.value)}, ${percentage(race.probabilityDelta.value)})`
          })
        }, {
          label: 'Seats Shifting Towards Dem',
          rows: mostImprovedDem.map((race) => {
            return {
              race: race.name,
              party: 'Democrats',
              gopSpent: race.gopExpenditures,
              gopMoneyChange: race.gopChange.value,
              demSpent: race.demExpenditures,
              demMoneyChange: race.demChange.value,
              winProb: 1 - race.finalProbability,
              perc: -(race.probabilityDelta.value)
            }
            // return `${race.name} (${money(race.demChange.value)}, ${percentage(race.probabilityDelta.value)})`
          })
        }
      ]
    }

    const mostImproved = this.expenditures.filter(r => r[this.strings.change].value > 0)
      .sort((a, b) => a.probabilityDelta.value > b.probabilityDelta.value ? -1 : 1).slice(0, 10)
    const mostTriaged = this.expenditures.filter(r => r[this.strings.change].value < 0)
      .sort((a, b) => a[this.strings.change].value < b[this.strings.change].value ? -1 : 1).slice(0, 10)
    return [
      {
        label: 'Most Improved Seats',
        description: 'Seats where the probability of a victory increased the most',
        rows: mostImproved.map((race) => {
          return {
            race: race.name,
            party: this.strings.party,
            gopSpent: race.gopExpenditures,
            gopMoneyChange: race.gopChange.value,
            demSpent: race.demExpenditures,
            demMoneyChange: race.demChange.value,
            winProb: this.strings.prop === 'gop' ? race.finalProbability : 1 - race.finalProbability,
            perc: race.probabilityDelta.value
          }
          // return `${race.name} (${money(race[this.strings.change].value)}, ${percentage(race.probabilityDelta.value)})`
        })
      }, {
        label: 'Most Triaged Seats',
        description: 'Seats where the most money was removed',
        rows: mostTriaged.map((race) => {
          return {
            race: race.name,
            party: this.strings.party,
            gopSpent: race.gopExpenditures,
            gopMoneyChange: race.gopChange.value,
            demSpent: race.demExpenditures,
            demMoneyChange: race.demChange.value,
            winProb: this.strings.prop === 'gop' ? race.finalProbability : 1 - race.finalProbability,
            perc: race.probabilityDelta.value
          }
          // return `${race.name} (${money(race[this.strings.change].value)}, ${percentage(race.probabilityDelta.value)})`
        })
      }
    ]
  },
  seatsChanged () {
    if (this.scenario.mode === 'god') {
      return this.expenditures.filter(r => r.gopChange.value !== 0 || r.demChange.value !== 0)
        .sort((a, b) => a.probabilityDelta.value > b.probabilityDelta.value ? -1 : 1)
    } else {
      return this.expenditures.filter(r => r[this.strings.change].value !== 0)
        .sort((a, b) => a.probabilityDelta.value > b.probabilityDelta.value ? -1 : 1)
    }
  },
  seatsLessThan () {
    if (this.scenario.mode === 'god') {
      return this.seatsChanged.filter(r => (r.gopChange.value !== 0 || r.demChange.value !== 0) && r.probabilityDelta.value > 0)
    }
    return this.seatsChanged.filter(r => r[this.strings.change].value < 0)
  },
  seatsGreaterThan () {
    if (this.scenario.mode === 'god') {
      return this.seatsChanged.filter(r => (r.gopChange.value !== 0 || r.demChange.value !== 0) && r.probabilityDelta.value < 0)
    }
    return this.seatsChanged.filter(r => r[this.strings.change].value > 0)
  },
  shareUrl () {
    return window.location.href
  },
  shareText () {
    const demSeats = (this.chamber === 'house' ? 200 : 53) - this.getOutcomeChange(this.summary.expected, 'GOP', this.chamber === 'house' ? 200 : 53)
    const gopSeats = (this.chamber === 'house' ? 235 : 47) - this.getOutcomeChange(this.summary.expected, 'Dem', this.chamber === 'house' ? 235 : 47)
    if (this.scenario.mode === 'gop') {
      const direction = gopSeats >= 0 ? 'gain' : 'lose'
      if (gopSeats === 0) {
        return `Check out my 2018 ${this.chamber} midterm simulation.`
      } else {
        return `Check out my 2018 ${this.chamber} midterm simulation where the GOP ${direction} ${Math.abs(gopSeats)} more seats.`
      }
    } else if (this.scenario.mode === 'dem') {
      const direction = demSeats >= 0 ? 'gain' : 'lose'
      if (demSeats === 0) {
        return `Check out my 2018 ${this.chamber} midterm simulation.`
      } else {
        return `Check out my 2018 ${this.chamber} midterm simulation where Democrats ${direction} ${Math.abs(demSeats)} more seats.`
      }
    } else {
      const party = gopSeats > demSeats ? 'The GOP' : 'Democrats'
      const partySeats = gopSeats > demSeats ? gopSeats : demSeats
      if (gopSeats === 0 && demSeats === 0) {
        return `Check out my 2018 ${this.chamber} midterm simulation.`
      } else {
        return `Check out my 2018 ${this.chamber} midterm simulation where ${party} gain ${Math.abs(partySeats)} more seats.`
      }
    }
  }
}

const methods = {
  ...mapActions({
    setChamber: 'calculator/setChamber',
    setMode: 'calculator/setMode'
  }),
  updateScenario (email) {
    this.updatingScenario = true
    API.patch(`scenarios/${this.scenario.reportId}/`, { email }).then(() => {
      this.updatingScenario = false
    })
  },
  getOutcomeChange (expected, party, actual) {
    if (expected.party === party) {
      return actual + expected.change
    } else {
      return actual - expected.change
    }
  },
  getColor (number) {
    if (number > 0) {
      return 'color: #57AEA6;'
    } else if (number < 0) {
      return 'color: #FA3138;'
    } else {
      return 'color: #666;'
    }
  }
}

const components = {
  ExpenditureShare,
  ExpendituresTable,
  Page,
  LoginSignup
}

export default {
  mixins,
  computed,
  methods,
  components,
  metaInfo: {
    title: 'Forecasting Spending Simulator - Report',
    meta: [
      {
        vmid: 'og:title',
        name: 'og:title',
        content: 'Forecasting Spending Simulator - Report'
      }, {
        vmid: 'twitter:title',
        name: 'twitter:title',
        content: 'Forecasting Spending Simulator - Report'
      }, {
        vmid: 'description',
        name: 'description',
        content: 'Output for a modified scenario of the 2018 U.S. midterm Congressional elections using the  Øptimus forecasting simulator. Includes modified chance of victories for each race based on money reallocation and the aggregate expected win differential for Republicans and Democrats.'
      }, {
        vmid: 'og:description',
        name: 'og:description',
        content: 'Output for a modified scenario of the 2018 U.S. midterm Congressional elections using the  Øptimus forecasting simulator. Includes modified chance of victories for each race based on money reallocation and the aggregate expected win differential for Republicans and Democrats.'
      }, {
        vmid: 'twitter:description',
        name: 'twitter:description',
        content: 'Output for a modified scenario of the 2018 U.S. midterm Congressional elections using the  Øptimus forecasting simulator. Includes modified chance of victories for each race based on money reallocation and the aggregate expected win differential for Republicans and Democrats.'
      }, {
        vmid: 'og:image',
        name: 'og:image',
        content: 'https://s3.us-east-2.amazonaws.com/forecasting.decisiondeskhq.com/spending-simulator-share.png'
      }, {
        vmid: 'og:image:secure_url',
        name: 'og:image:secure_url',
        content: 'https://s3.us-east-2.amazonaws.com/forecasting.decisiondeskhq.com/spending-simulator-share.png'
      }, {
        vmid: 'twitter:image',
        name: 'twitter:image',
        content: 'https://s3.us-east-2.amazonaws.com/forecasting.decisiondeskhq.com/spending-simulator-share.png'
      }
    ]
  },
  data () {
    return {
      loading: false,
      updatingScenario: false,
      scenario: null,
      expenditures: [],
      default: []
    }
  },
  beforeMount () {
    if (this.$route.params.scenario && this.$route.params.expenditures && this.$route.params.default) {
      this.default = this.$route.params.default
      this.scenario = this.$route.params.scenario
      this.setChamber(this.$route.params.chamber)
      this.setMode(this.scenario.mode)
      this.expenditures = this.$route.params.expenditures.map(item => this.calculateItem(item))
    } else if (this.$route.query.id) {
      const promises = []
      let expenditures = []
      promises.push(
        API.get('expenditures/').then((data) => {
          this.default = data
        })
      )
      promises.push(
        API.get(`scenarios/${this.$route.query.id}/`).then((data) => {
          this.scenario = data
          this.setChamber(this.scenario.chamber || this.scenario.expenditures[0].chamber)
          expenditures = data.expenditures.filter(item => item.chamber === this.chamber)
        })
      )
      this.loading = true
      Promise.all(promises).then(() => {
        const existingRaces = expenditures.map(r => r.race)
        this.default.forEach((row) => {
          if (existingRaces.includes(row.race) === false) {
            expenditures.push(row)
          }
        })
        this.expenditures = expenditures.map(item => this.calculateItem(item))
        this.loading = false
      })
    } else {
      // redirect to calculator
      // this.$router.replace({ name: 'calculator' })
    }
  }
}
</script>

<style>
.report-overview {
  max-width: 1000px;
  margin: 0 auto;
}

.outcome-party, .outcome-score {
  font-size: 1.2em;
  font-weight: 600;
  margin: 0 5px;
}

.outcome-score {
  color: #bdbdbd;
}

.list-header {
  margin-top: 0px;
}

.summary-item {
  display: block;
  margin: 10px 0;
  text-align: center;
}

.summary-item-label {
  display: inline-block;
}

.summary-item-label, .list-header {
  font-size: 1em;
  letter-spacing: 0.05em;
  font-weight: 600;
  color: #333;
}

.summary-item-value {
  font-weight: 600;
}

.summary-item-value, .list-item {
  font-size: 1.1em;
  margin: 8px;
}

.top-list-wrapper {
  /* display: flex;
  justify-content: center; */
  margin: 30px 0;
  text-align: center;
}

.list-wrapper {
  display: inline-block;
  vertical-align: top;
}

.list-description {
  font-size: 0.9em;
  font-style: italic;
  color: #666;
}

.list-item {
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: 0.9em;
  margin: 16px;
  text-align: start;
}

.list-number {
  font-size: 1.6em;
  font-weight: 600;
  margin-right: 20px;
  color: #FA3138;
}

.list-item-key {
  font-size: 0.9em;
  font-weight: 400;
  color: #666;
}

.list-item-text {
  font-weight: 500;
  font-size: 1.1em;
}

@media only screen and (max-width: 420px) {
  .list-wrapper {
    margin: 0;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/Scenario.vue